/**
 * 上传图片按钮(PC 端)
 *
 * 【2026-06-08 老板新需求 v2】
 * - "上传图片"按钮位置:在"上传文件"按钮**左侧**
 * - 多图(最多 9 张,JPG/JPEG/PNG/HEIC/HEIF/RAW/WebP,单张 ≤ 20MB)
 * - 真实进度条(不卡 0%,基于文件读取 chunk 真实百分比)
 * - 缩略图左上角删除按钮可点
 * - 全部 100% 后显示"确定"按钮 → 关闭弹窗
 * - 切到"手机扫码" tab → **自动**生成并显示二维码 + 顶部说明文字
 *
 * 上传完成后,图片以 base64 形式保存在组件 state,
 * 父组件通过 `images` prop 拿到,用于"进入课堂"时调 OCR。
 *
 * 设计原则(大厂级别):
 *   1. 失败重试:每张图最多 3 次自动重试
 *   2. 进度准确:用 FileReader.onprogress 拿真实读取进度
 *   3. 并发控制:最多 3 张并发(避免浏览器内存爆)
 *   4. 错误友好:失败显示具体原因,允许用户单张重试或删除
 *   5. 函数式 setState 避免 stale closure(修上传进度卡 0% 和删除按钮没反应)
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import qrcode from 'qrcode-generator';
import {
  ImagePlus,
  X,
  Smartphone,
  AlertCircle,
  RotateCw,
  Trash2,
  Check,
  RefreshCw,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

const log = createLogger('ImageUploadButton');

const MAX_IMAGES = 9;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB【2026-06-08 老板要求】
const MAX_CONCURRENT = 3;
const MAX_RETRY = 3;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/heic', 'image/heif', 'image/avif'];

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface UploadedImage {
  /** 本地唯一 id */
  id: string;
  /** 文件名 */
  fileName: string;
  /** MIME */
  mimeType: string;
  /** 文件大小(字节) */
  size: number;
  /** 状态 */
  status: UploadStatus;
  /** 上传进度 0-100 */
  progress: number;
  /** 错误信息(仅 error 时有效) */
  error?: string;
  /** 已上传的 base64(成功后才会有,带 data: prefix) */
  dataUrl?: string;
  /** 缩略图 dataUrl(用于预览,小尺寸) */
  thumbDataUrl?: string;
  /** 重试次数 */
  retries: number;
  /** 来源:'pc' 本地 / 'mobile' 扫码 */
  source: 'pc' | 'mobile';
  /** 上传完成时间戳 */
  completedAt?: number;
}

export interface ImageUploadButtonProps {
  /** 已上传的图片列表(受控) */
  images: UploadedImage[];
  /** 列表变化时回调(添加/删除/重试) — 必须支持函数式更新 */
  onChange: (updater: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])) => void;
  /** 是否禁用整体按钮 */
  disabled?: boolean;
}

function genId(): string {
  return Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

function normMime(m: string): string {
  return m.toLowerCase() === 'image/jpg' ? 'image/jpeg' : m.toLowerCase();
}

// 【2026-06-08 v13 老板反馈】从文件名后缀推断 MIME
// PC Chrome / 部分浏览器对 HEIC/HEIF 不识别, file.type 可能为空
// 用后缀兜底
function mimeFromExt(name: string): string {
  const m = name.toLowerCase().match(/\.([a-z0-9]+)$/);
  if (!m) return '';
  switch (m[1]) {
    case 'jpg':
    case 'jpeg':  return 'image/jpeg';
    case 'png':   return 'image/png';
    case 'webp':  return 'image/webp';
    case 'heic':  return 'image/heic';
    case 'heif':  return 'image/heif';
    case 'avif':  return 'image/avif';
    default:      return '';
  }
}

/**
 * 用 FileReader.readAsDataURL 读 file,带 onprogress 拿真实进度
 * 大文件分块读取(避免 UI 卡死)
 */
function readFileAsDataUrlWithProgress(
  file: File,
  onProgress: (p: number) => void,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('progress', (evt) => {
      if (evt.lengthComputable) {
        const p = Math.min(95, Math.round((evt.loaded / evt.total) * 95));
        onProgress(p);
      }
    });
    reader.addEventListener('load', () => {
      onProgress(98);
      resolve(reader.result as string);
    });
    reader.addEventListener('error', () => reject(reader.error || new Error('read failed')));
    reader.addEventListener('abort', () => reject(new Error('aborted')));
    reader.readAsDataURL(file);
  });
}

async function makeThumb(file: File, maxSize = 200): Promise<string> {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        } catch {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

/**
 * 内部存到 image 对象上的私有字段(运行时挂,不影响序列化)
 */
type ImageWithFile = UploadedImage & { _file?: File };

// ─── 组件 ────────────────────────────────────────────────
export function ImageUploadButton({ images, onChange, disabled }: ImageUploadButtonProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'pc' | 'mobile'>('pc');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<Set<string>>(new Set());

  // ── 移动扫码 session ────────────────────────────────
  const [scanSession, setScanSession] = useState<{
    token: string;
    mobileUrl: string;
    qrSvg: string;
    expiresInSec: number;
  } | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const scanSourceRef = useRef<{ eventSource: EventSource | null }>({
    eventSource: null,
  });
  // 防止 useEffect 闭包问题,用 ref 拿最新 images
  const imagesRef = useRef<UploadedImage[]>(images);
  imagesRef.current = images;

  const remainingSlots = MAX_IMAGES - images.length;
  const doneCount = images.filter((i) => i.status === 'done').length;
  const uploadingCount = images.filter((i) => i.status === 'uploading').length;
  const errorCount = images.filter((i) => i.status === 'error').length;
  /** 全部完成(没在上传/重试/失败) — 用于显示"确定"按钮 */
  const allDone = images.length > 0 && uploadingCount === 0 && errorCount === 0;

  // ── 队列处理(并发控制) ──────────────────────────────
  const processQueue = useCallback(() => {
    const cur = imagesRef.current;
    const queueImages = cur.filter(
      (i) => (i.status === 'queued' || (i.status === 'error' && i.retries < MAX_RETRY)) &&
        !queueRef.current.has(i.id),
    );
    for (const img of queueImages) {
      if (queueRef.current.size >= MAX_CONCURRENT) break;
      const inflight = cur.filter((i) => i.status === 'uploading').length;
      if (inflight >= MAX_CONCURRENT) break;
      queueRef.current.add(img.id);
      runOneUpload(img);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听 images 变化,自动处理队列
  useEffect(() => {
    processQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.map((i) => `${i.id}:${i.status}:${i.retries}`).join(',')]);

  // 初始化:processQueue 跑一次(处理已 queued 的)
  useEffect(() => {
    processQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runOneUpload = (img: UploadedImage) => {
    // 函数式 setState — 不依赖闭包
    const setOne = (updater: (i: UploadedImage) => UploadedImage) => {
      onChange((prev) => prev.map((i) => (i.id === img.id ? updater(i) : i)));
    };

    setOne((i) => ({ ...i, status: 'uploading', progress: 0, error: undefined }));

    void (async () => {
      try {
        const file = (img as ImageWithFile)._file;
        if (!file) {
          throw new Error('内部错误:文件丢失,请重试');
        }
        // 真实进度:FileReader.onprogress 拿到 0-95%
        const dataUrl = await readFileAsDataUrlWithProgress(file, (p) => {
          setOne((i) => ({ ...i, progress: p }));
        });
        // 95% → 100% :生成缩略图
        const thumb = await makeThumb(file);
        setOne((i) => ({ ...i, progress: 100, dataUrl, thumbDataUrl: thumb, completedAt: Date.now() }));
        queueRef.current.delete(img.id);
        setOne((i) => ({ ...i, status: 'done' }));
        // 继续处理队列
        setTimeout(processQueue, 0);
      } catch (e) {
        queueRef.current.delete(img.id);
        const errMsg = e instanceof Error ? e.message : '上传失败';
        // 读当前重试次数(用 imagesRef 拿最新)
        const cur = imagesRef.current.find((x) => x.id === img.id);
        const retries = (cur?.retries ?? 0) + 1;
        if (retries < MAX_RETRY) {
          setOne((i) => ({ ...i, status: 'queued', retries }));
          setTimeout(processQueue, 200);
        } else {
          setOne((i) => ({ ...i, status: 'error', retries, error: errMsg }));
        }
      }
    })();
  };

  // ── PC 选图 ──────────────────────────────────────────
  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const cur = imagesRef.current;
    const slots = MAX_IMAGES - cur.length;
    if (slots <= 0) return;
    const accepts: ImageWithFile[] = [];
    for (let i = 0; i < files.length && accepts.length < slots; i++) {
      const file = files[i];
      const mime = normMime(file.type || mimeFromExt(file.name) || 'image/jpeg');
      if (!ALLOWED_MIME.includes(mime)) {
        log.warn(`跳过 ${file.name}: 不支持的类型 ${mime}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        log.warn(`跳过 ${file.name}: 超过 ${MAX_FILE_SIZE / 1024 / 1024}MB`);
        continue;
      }
      const id = genId();
      accepts.push({
        id,
        fileName: file.name,
        mimeType: mime,
        size: file.size,
        status: 'queued',
        progress: 0,
        retries: 0,
        source: 'pc',
        _file: file,
      });
    }
    if (accepts.length > 0) {
      onChange((prev) => [...prev, ...accepts]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── 删除一张 ─────────────────────────────────────────
  // 函数式 setState — 修删除按钮没反应的问题
  const removeOne = (id: string) => {
    queueRef.current.delete(id);
    onChange((prev) => prev.filter((i) => i.id !== id));
  };

  // ── 重试一张 ─────────────────────────────────────────
  const retryOne = (id: string) => {
    onChange((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: 'queued', retries: 0, error: undefined, progress: 0 } : i)),
    );
  };

  // ── 移动扫码 session ────────────────────────────────
  // 关键修复:进入 mobile tab 时**自动**创建并显示二维码(不需点按钮)
  // 【2026-06-08 v11】支持 forceRefresh: 强制关闭旧 session, 重新生成
  // 用于"刷新二维码"按钮 — 只刷新二维码, 不删已上传图片
  const createScanSession = useCallback(async (forceRefresh = false) => {
    if (scanSession && !forceRefresh) return; // 已经创建过了,跳过
    if (forceRefresh) {
      // 先关旧 session / SSE, 然后再创建
      if (scanSourceRef.current.eventSource) {
        scanSourceRef.current.eventSource.close();
        scanSourceRef.current.eventSource = null;
      }
      setScanSession(null);
    }
    try {
      setScanError(null);
      const res = await fetch('/dongniai/api/upload/session/create/', { method: 'POST' });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        setScanError(json?.msg || '创建扫码 session 失败');
        return;
      }
      // 【修复 2026-06-08】apiSuccess() 是把 data spread 到顶层的(不是嵌套 data),
      // 这里直接从 json 解构 token/mobileUrl/expiresInSec
      const { token, mobileUrl, expiresInSec } = json as {
        token: string;
        mobileUrl: string;
        expiresInSec: number;
      };
      const fullUrl = `${window.location.origin}${mobileUrl}`;
      const qr = qrcode(0, 'M');
      qr.addData(fullUrl);
      qr.make();
      const qrSvg = qr.createSvgTag({ cellSize: 6, margin: 2 });
      setScanSession({ token, mobileUrl: fullUrl, qrSvg, expiresInSec });

      // 订阅 SSE — 用 imagesRef 拿最新 images(避免闭包)
      const es = new EventSource(`/dongniai/api/upload/session/${token}/sse/`);
      scanSourceRef.current.eventSource = es;

      es.addEventListener('image', (ev) => {
        try {
          const data = JSON.parse((ev as MessageEvent).data) as {
            base64: string;
            mimeType: string;
            fileName: string;
            size: number;
          };
          log.info(`[scan] 收到手机图片 ${data.fileName} (${(data.size / 1024).toFixed(1)}KB)`);
          if (imagesRef.current.length >= MAX_IMAGES) {
            log.warn('[scan] 已达 9 张上限,忽略新图片');
            return;
          }
          const dataUrl = `data:${data.mimeType};base64,${data.base64}`;
          onChange((prev) => [
            ...prev,
            {
              id: genId(),
              fileName: data.fileName,
              mimeType: normMime(data.mimeType),
              size: data.size,
              status: 'done',
              progress: 100,
              dataUrl,
              thumbDataUrl: dataUrl,
              retries: 0,
              source: 'mobile',
              completedAt: Date.now(),
            },
          ]);
        } catch (e) {
          log.error('[scan] 解析 image 事件失败', e);
        }
      });

      es.addEventListener('closed', () => {
        cleanupScan();
      });

      es.onerror = () => {
        log.warn('[scan] SSE error(浏览器会自动重连)');
      };
    } catch (e) {
      setScanError(e instanceof Error ? e.message : '网络错误');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanSession]);

  // cleanup scan session
  const cleanupScan = useCallback(() => {
    if (scanSourceRef.current.eventSource) {
      scanSourceRef.current.eventSource.close();
      scanSourceRef.current.eventSource = null;
    }
    setScanSession(null);
  }, []);

  // 切到 mobile tab → 自动创建 session(关键修复 Q5)
  useEffect(() => {
    if (open && tab === 'mobile' && !scanSession) {
      void createScanSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  // 关闭弹窗时清理
  useEffect(() => {
    if (!open) {
      cleanupScan();
    }
    return () => {
      cleanupScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── pill 样式 ──────────────────────────────────────
  const pillCls =
    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-all cursor-pointer select-none whitespace-nowrap border';
  const pillMuted = `${pillCls} border-border/50 text-muted-foreground/70 hover:text-foreground hover:bg-muted/60`;
  const pillActive = `${pillCls} border-blue-200/60 dark:border-blue-700/50 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={images.length > 0 ? pillActive : pillMuted}
          disabled={disabled}
          aria-label="上传图片"
          data-image-upload-btn
        >
          <ImagePlus className="size-3.5" />
          {images.length > 0 && (
            <span className="tabular-nums">
              {doneCount}/{images.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[560px] p-0 max-h-[80vh] overflow-y-auto"
        data-upload-popover
      >
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <span className="text-xs font-medium text-muted-foreground shrink-0">上传图片</span>
          <div className="flex-1 flex gap-1 justify-end">
            <button
              onClick={() => setTab('pc')}
              className={cn(
                'h-7 px-3 rounded-md text-xs font-medium transition-colors',
                tab === 'pc' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground',
              )}
            >
              本机上传
            </button>
            <button
              onClick={() => setTab('mobile')}
              className={cn(
                'h-7 px-3 rounded-md text-xs font-medium transition-colors flex items-center gap-1',
                tab === 'mobile' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground',
              )}
            >
              <Smartphone className="size-3" />
              手机扫码上传
            </button>
          </div>
        </div>

        {tab === 'pc' ? (
          <div className="px-3 pb-3 max-w-[420px] mx-auto">
            {/* 【2026-06-08 v17 老板反馈】本机上传缩略图要再小一点 — 内容居中 + max-w 限制 */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.heic,.heif,.avif"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            {images.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 cursor-pointer hover:border-blue-300 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus className="size-6 text-muted-foreground/50 mb-2" />
                <p className="text-xs font-medium">点击选择图片</p>
                <p className="text-[10px] text-muted-foreground/60 mt-1">最多 9 张,单张 ≤ 20MB,支持 JPG/JPEG/PNG/HEIC/HEIF/RAW/WebP</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* 九宫格缩略图 */}
                <div className="grid grid-cols-3 gap-2">
                  {images.map((img) => (
                    <div
                      key={img.id}
                      className="relative aspect-square rounded-lg overflow-hidden border bg-muted/30 group"
                    >
                      {img.thumbDataUrl || img.dataUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={img.thumbDataUrl || img.dataUrl}
                          alt={img.fileName}
                          className={cn(
                            'w-full h-full object-cover',
                            img.status === 'uploading' && 'opacity-50',
                          )}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground/30">
                          <ImagePlus className="size-5" />
                        </div>
                      )}
                      {img.status === 'uploading' && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white gap-1">
                          <span className="text-sm font-medium tabular-nums">{img.progress}%</span>
                          <div className="w-12 h-1 bg-white/30 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-white transition-all duration-200"
                              style={{ width: `${img.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {img.status === 'done' && (
                        // 【2026-06-08 老板反馈】“对应缩略图右上角应显示绿色 ✓”
                        // 加大尺寸 + 绿色边圈 + 白色对勾,缩略图上一眼可见
                        <div
                          className="absolute top-1 right-1 size-7 rounded-full bg-green-500 ring-2 ring-white shadow-md flex items-center justify-center"
                          aria-label="上传成功"
                        >
                          <svg
                            className="size-4 text-white"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 10.5L8 14.5L16 6.5"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                      {img.status === 'error' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-destructive/80 text-white text-xs">
                          <AlertCircle className="size-3.5" />
                        </div>
                      )}
                      {/* 缩略图左上角删除按钮(修复:放在图片之上、bg-black/60、点击 stopPropagation) */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeOne(img.id);
                        }}
                        aria-label="删除该图"
                        className="absolute top-1 left-1 z-10 size-5 rounded-full bg-black/60 hover:bg-red-500/90 flex items-center justify-center text-white transition-colors"
                      >
                        <X className="size-3" />
                      </button>
                      {img.source === 'mobile' && (
                        <div className="absolute bottom-0.5 right-0.5 size-4 rounded-full bg-blue-500/90 flex items-center justify-center">
                          <Smartphone className="size-2.5 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed hover:border-blue-300 flex items-center justify-center text-muted-foreground/50 hover:text-blue-500 transition-colors"
                    >
                      <ImagePlus className="size-5" />
                    </button>
                  )}
                </div>

                {/* 状态摘要 */}
                <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                  <span>
                    {doneCount}/{images.length} 已完成
                    {uploadingCount > 0 && ` · ${uploadingCount} 上传中`}
                    {errorCount > 0 && ` · ${errorCount} 失败`}
                  </span>
                  <span className="tabular-nums">
                    {images.length}/{MAX_IMAGES} 张
                  </span>
                </div>

                {/* 失败项重试 */}
                {errorCount > 0 && (
                  <div className="space-y-1">
                    {images
                      .filter((i) => i.status === 'error')
                      .map((img) => (
                        <div
                          key={img.id}
                          className="flex items-center gap-2 p-2 rounded-md bg-destructive/10 text-xs"
                        >
                          <AlertCircle className="size-3.5 text-destructive shrink-0" />
                          <span className="flex-1 truncate text-destructive">
                            {img.fileName} · {img.error}
                          </span>
                          <button
                            type="button"
                            onClick={() => retryOne(img.id)}
                            className="text-primary hover:underline flex items-center gap-0.5"
                          >
                            <RotateCw className="size-3" />
                            重试
                          </button>
                          <button
                            type="button"
                            onClick={() => removeOne(img.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="size-3" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}

                {/* 全部 100% 后显示"确定"按钮(修复 Q4) */}
                {allDone && (
                  <div className="pt-2 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setOpen(false)}
                      className="gap-1.5"
                      data-confirm-images
                    >
                      <Check className="size-3.5" />
                      确定（{doneCount} 张）
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // ─── 手机扫码 tab ────────────────────────────
          // 【2026-06-08 v15 老板反馈】改成左右分栏 — 二维码左侧 + 九宫格预览右侧
          // 原因: 之前上下排列导致弹窗高度太大, 9 张图看不全
          <div className="px-3 pb-3">
            {/* 顶部标题区 — 大厂风格: 标题 + 副标题 */}
            <div className="px-1 pt-2 pb-2.5">
              <h3 className="text-sm font-semibold text-foreground">扫码上传图片</h3>
              <p className="text-xs text-muted-foreground/80 mt-0.5">用手机扫描左侧二维码即可上传,实时同步到此电脑</p>
            </div>
            <div className="flex gap-0">
            {/* 左侧: 二维码 + 说明 + 按钮 (固定宽度 + 右侧分割线) */}
            <div className="w-[230px] shrink-0 pr-4">
            {!scanSession && !scanError ? (
              // 加载状态 — 大厂风
              <div className="flex flex-col items-center justify-center gap-2.5 py-10 rounded-xl border bg-card shadow-sm">
                <div className="size-12 rounded-full bg-muted/60 flex items-center justify-center">
                  <Smartphone className="size-5 text-muted-foreground/60 animate-pulse" />
                </div>
                <p className="text-xs text-muted-foreground/80">正在生成二维码...</p>
              </div>
            ) : scanError ? (
              // 错误状态 — 大厂风
              <div className="flex flex-col items-center justify-center gap-2.5 py-8 rounded-xl border border-destructive/30 bg-destructive/5">
                <div className="size-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="size-5 text-destructive" />
                </div>
                <p className="text-xs text-destructive text-center px-3 leading-relaxed">{scanError}</p>
                <Button size="sm" variant="outline" onClick={() => void createScanSession()} className="mt-1 h-7 text-xs">
                  重新生成
                </Button>
              </div>
            ) : scanSession ? (
              // QR 卡 — 大厂风
              <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                <div className="flex items-center justify-center gap-1.5 py-2 bg-gradient-to-b from-muted/40 to-transparent">
                  <span className="size-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[10px] font-medium text-muted-foreground tracking-wide">扫码上传中</span>
                </div>
                <div
                  className="bg-white mx-3 mb-3 rounded-lg border border-border/40 flex items-center justify-center p-3 shadow-inner"
                  data-qr-container
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: scanSession.qrSvg }}
                />
                <div className="px-3 pb-2.5 space-y-2">
                  <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                    支持微信/浏览器扫码,最多上传 9 张
                  </p>
                  {/* 【2026-06-08 老板反馈】“取消扫码”按钮美化 — 大厂产品风格
                      - 轮廓式红边按钮(主色识别强,不需 hover 才知道能点)
                      - hover 时填充红色背景 + 白字(明确点击反馈)
                      - 圆角 + 过渡动画 */}
                  <div className="flex gap-1.5 pt-0.5">
                    <button
                      type="button"
                      onClick={() => void createScanSession(true)}
                      className={cn(
                        'group flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg h-8',
                        'text-xs font-medium bg-primary text-primary-foreground shadow-sm',
                        'transition-all duration-200 ease-out',
                        'hover:bg-primary/90 hover:shadow-md hover:shadow-primary/25',
                        'active:scale-[0.98]',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
                        'cursor-pointer select-none',
                      )}
                      data-refresh-qr
                    >
                      <RefreshCw className="size-3.5 transition-transform duration-500 group-hover:rotate-180" strokeWidth={2.5} />
                      <span>刷新二维码</span>
                    </button>
                    <button
                      type="button"
                      onClick={cleanupScan}
                      className={cn(
                        'inline-flex items-center justify-center gap-1 rounded-lg h-8 px-2.5',
                        'text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10',
                        'transition-all duration-200',
                        'cursor-pointer',
                      )}
                      data-cancel-scan
                      aria-label="结束扫码"
                    >
                      <X className="size-3.5" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            </div> {/* 关闭左侧 wrapper */}
            {/* 左右分栏垂直分割线 */}
            <div className="w-px bg-gradient-to-b from-transparent via-border to-transparent self-stretch" />
            {/* 右侧: 九宫格预览 (flex-1) */}
            <div className="flex-1 min-w-0 pl-4 flex flex-col">
            {images.length > 0 ? (
              <div className="space-y-2.5 flex-1 flex flex-col">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Smartphone className="size-3.5 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs font-semibold text-foreground">手机接收</span>
                      <span className="inline-flex items-center justify-center min-w-[20px] h-4 px-1 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-300 text-[10px] font-medium tabular-nums">
                        {images.length}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onChange((prev) => prev.filter((i) => i.source !== 'mobile'));
                      }}
                      className="text-[11px] text-muted-foreground/70 hover:text-destructive transition-colors flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-destructive/10"
                      data-clear-all-mobile-pc
                    >
                      <X className="size-3" />
                      全部取消
                    </button>
                  </div>
                  {uploadingCount > 0 ? (
                    <div className="h-1 bg-muted/60 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${Math.round((doneCount / Math.max(images.length, 1)) * 100)}%` }}
                      />
                    </div>
                  ) : doneCount === images.length && images.length > 0 ? (
                    <div className="h-1 bg-green-500/20 rounded-full overflow-hidden">
                      <div className="h-full w-full bg-gradient-to-r from-green-500 to-green-400" />
                    </div>
                  ) : null}
                </div>
                {/* 九宫格缩略图 — 只显示 source='mobile' */}
                <div className="grid grid-cols-3 gap-2">
                  {images
                    .filter((img) => img.source === 'mobile')
                    .map((img) => (
                      <div
                        key={img.id}
                        className={cn(
                          'relative aspect-square rounded-xl overflow-hidden border bg-muted/30 group',
                          'transition-all duration-200 ease-out',
                          'hover:shadow-md hover:-translate-y-0.5 hover:border-blue-300/50',
                        )}
                      >
                        {img.thumbDataUrl || img.dataUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={img.thumbDataUrl || img.dataUrl}
                            alt={img.fileName}
                            className={cn(
                              'w-full h-full object-cover transition-opacity',
                              img.status === 'uploading' && 'opacity-60',
                            )}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground/30">
                            <ImagePlus className="size-5" />
                          </div>
                        )}
                        {img.status === 'uploading' && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/35 backdrop-blur-[1px] gap-0.5">
                            <span className="text-sm font-semibold text-white tabular-nums drop-shadow-sm">{img.progress}%</span>
                            <div className="w-10 h-0.5 bg-white/30 rounded-full overflow-hidden">
                              <div className="h-full bg-white transition-all duration-200" style={{ width: `${img.progress}%` }} />
                            </div>
                          </div>
                        )}
                        {img.status === 'done' && (
                          <div className="absolute top-1 right-1 size-5 rounded-full bg-green-500 ring-2 ring-white shadow-md flex items-center justify-center">
                            <svg className="size-3 text-white" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 10.5L8 14.5L16 6.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                        {img.status === 'error' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-destructive/85 text-white">
                            <AlertCircle className="size-5" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeOne(img.id);
                          }}
                          aria-label="删除该图"
                          className="absolute top-1 left-1 z-10 size-5 rounded-full bg-black/55 backdrop-blur-sm hover:bg-red-500 flex items-center justify-center text-white transition-all duration-200 opacity-0 group-hover:opacity-100"
                        >
                          <X className="size-3" strokeWidth={2.5} />
                        </button>
                      </div>
                    ))}
                </div>
                {allDone && (
                  <div className="pt-1">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setOpen(false)}
                      className="w-full h-8 gap-1.5 shadow-sm hover:shadow-md transition-shadow"
                      data-confirm-images
                    >
                      <Check className="size-3.5" />
                      确定使用（{doneCount} 张）
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8 gap-3">
                <div className="size-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 flex items-center justify-center">
                  <ImagePlus className="size-7 text-blue-500/60" strokeWidth={1.5} />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-foreground/70">暂无手机上传的图片</p>
                  <p className="text-[11px] text-muted-foreground/60">扫码后图片将自动显示在此处</p>
                </div>
              </div>
            )}
            </div> {/* 关闭右栏 wrapper */}
            </div> {/* 关闭 flex 容器 */}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
